import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  IconButton,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  PeopleOutlined,
  SearchOutlined,
  VisibilityOutlined,
  EditOutlined,
  PersonAddOutlined,
} from '@mui/icons-material';
import { api } from '../../services/api';
import { authService } from '../../services/authService';
import { ROLES } from '../../constants/roles';
import { Partner } from '../../types/api';

const CustomerList: React.FC = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Partner[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Partner[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const currentUser = authService.getUser();
  const isAdmin = currentUser?.role === ROLES.ADMIN;

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    filterCustomers();
  }, [searchQuery, customers]);

  const fetchCustomers = async () => {
    try {
      const params: { type: string; include_disabled?: boolean } = { type: 'CUSTOMER' };
      if (isAdmin) {
        params.include_disabled = true;
      }
      const response = await api.get('/api/partners', { params });
      console.log('Customer API response:', response.data);
      const customerData = response.data.partners || [];
      console.log('Parsed customers:', customerData);
      setCustomers(Array.isArray(customerData) ? customerData : []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterCustomers = () => {
    if (!Array.isArray(customers)) {
      setFilteredCustomers([]);
      return;
    }
    if (!searchQuery.trim()) {
      setFilteredCustomers(customers);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = customers.filter((customer) => {
      return (
        customer.name.toLowerCase().includes(query) ||
        (customer.city && customer.city.toLowerCase().includes(query)) ||
        (customer.district && customer.district.toLowerCase().includes(query)) ||
        (customer.state && customer.state.toLowerCase().includes(query))
      );
    });
    setFilteredCustomers(filtered);
  };

  return (
    <Paper sx={{ p: 2, m: 0 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PeopleOutlined sx={{ fontSize: 28, mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="bold">
            All Customers
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<PersonAddOutlined />}
          onClick={() => navigate('/customers/new')}
        >
          Add Customer
        </Button>
      </Box>

      {/* Search Bar */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search by name, city, district, or state..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlined color="action" fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {/* Results Count */}
      {searchQuery && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Found {filteredCustomers.length} customer{filteredCustomers.length !== 1 ? 's' : ''} matching "{searchQuery}"
        </Typography>
      )}

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Customer Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>City</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>District</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>State</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCustomers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchQuery ? 'No customers found matching your search' : 'No customers found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCustomers.map((customer) => (
                  <TableRow key={customer.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {customer.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{customer.city || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{customer.district || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{customer.state || '-'}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="info"
                        title="View Customer"
                        onClick={() => navigate(`/customers/${customer.id}`)}
                      >
                        <VisibilityOutlined fontSize="small" />
                      </IconButton>
                      {(isAdmin || authService.hasPermission('customers_edit')) && (
                        <IconButton
                          size="small"
                          color="primary"
                          title="Edit Customer"
                          onClick={() => navigate(`/customers/${customer.id}/edit`)}
                        >
                          <EditOutlined fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Total Count */}
      {!loading && !searchQuery && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Total: {customers.length} customer{customers.length !== 1 ? 's' : ''}
        </Typography>
      )}
    </Paper>
  );
};

export default CustomerList;
